import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Users, Award, Heart, Check, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

import { API } from "../config/api";

const ICON_MAP = { Users, Award, Heart };

export default function SponsorsPage() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/site-pages/sponsors`)
      .then(res => res.json())
      .then(data => {
        if (!data.not_found) setPageData(data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Helper to get section by type
  const getSection = (type) => pageData?.sections?.find(s => s.type === type);

  const heroSection = getSection("hero") || {
    heading: "Become a Sponsor",
    subheading: "PARTNERSHIP OPPORTUNITY",
    body: "Partner with Kerala Hindus London and connect with our vibrant community while supporting cultural preservation."
  };

  const whySection = getSection("why_sponsor") || {
    heading: "Why Sponsor?",
    items: [
      { icon: "Users", title: "Community Reach", body: "Connect with families in the Kerala Hindu community of London Ontario." },
      { icon: "Award", title: "Brand Visibility", body: "Recognition through banners, social media, and event promotions." },
      { icon: "Heart", title: "Community Impact", body: "Support cultural preservation and community bonding initiatives." }
    ]
  };

  const tiersSection = getSection("tiers") || {
    heading: "Sponsorship Tiers",
    body: "Choose a sponsorship level that fits your organization. All sponsors receive recognition and visibility at our events.",
    items: []
  };

  const ctaSection = getSection("cta") || {
    heading: "Ready to Sponsor?",
    body: "Contact us to discuss sponsorship opportunities and customize a package that works for your organization.",
    cta_text: "Contact Us",
    cta_link: "/contact"
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="pt-20 bg-kerala-cream" data-testid="sponsors-page">
      {/* Hero */}
      <section className="py-20 bg-kerala-green">
        <div className="section-container text-center">
          <p className="text-kerala-gold text-sm tracking-widest uppercase mb-4">{heroSection.subheading}</p>
          <h1 className="font-heading text-5xl md:text-6xl text-white mb-6">
            {heroSection.heading}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {heroSection.body}
          </p>
        </div>
      </section>

      {/* Why Sponsor */}
      <section className="section-padding bg-kerala-cream">
        <div className="section-container">
          <h2 className="font-heading text-3xl text-kerala-green text-center mb-12">
            {whySection.heading}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whySection.items?.map((item, idx) => {
              const IconComponent = ICON_MAP[item.icon] || Users;
              return (
                <div key={idx} className="text-center p-6 bg-white rounded-lg shadow-sm border border-kerala-gold/10">
                  <div className="w-16 h-16 bg-kerala-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-8 h-8 text-kerala-green" />
                  </div>
                  <h3 className="font-heading text-xl text-kerala-green mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sponsorship Tiers */}
      {tiersSection.items?.length > 0 && (
        <section className="section-padding bg-kerala-cream-alt">
          <div className="section-container">
            <div className="text-center mb-12">
              <h2 className="font-heading text-4xl text-kerala-green mb-4">{tiersSection.heading}</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">{tiersSection.body}</p>
            </div>

            <div className="space-y-8">
              {tiersSection.items.map((tier, idx) => (
                <div 
                  key={idx} 
                  className={`rounded-xl overflow-hidden shadow-lg ${tier.bgColor || "bg-white"}`}
                  data-testid={`tier-${tier.name?.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {/* Tier Header */}
                  <div className={`p-6 text-center ${tier.gradient || "bg-kerala-green"}`}>
                    <h3 className="font-heading text-2xl text-white mb-1">{tier.name}</h3>
                    {tier.subtitle && (
                      <p className="text-white/80 text-sm">{tier.subtitle}</p>
                    )}
                  </div>

                  {/* Benefits */}
                  <div className="p-8">
                    <ul className="space-y-4">
                      {tier.benefits?.map((benefit, bidx) => (
                        <li key={bidx} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-kerala-green shrink-0 mt-0.5" />
                          <span className="text-gray-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-kerala-terracotta">
        <div className="section-container text-center">
          <h2 className="font-heading text-4xl text-white mb-4">
            {ctaSection.heading}
          </h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto">
            {ctaSection.body}
          </p>
          <Link to={ctaSection.cta_link || "/contact"}>
            <Button className="bg-white text-kerala-terracotta hover:bg-kerala-cream rounded-full px-8 py-4 font-medium">
              {ctaSection.cta_text || "Contact Us"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
